import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, ChevronDown, HelpCircle, MessageCircle } from 'lucide-react';
import { cn } from '../lib/utils';
import { faqCategories, getAllFaqItems } from '../data/faqData';

/**
 * FAQ Accordion Item Component
 */
const FaqItem = ({ question, answer, isOpen, onToggle, index }) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <div className="border-b border-black/[0.06] last:border-b-0">
      <button
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        className="w-full flex items-center justify-between py-5 px-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--primary))] focus-visible:ring-offset-2 rounded-lg bg-transparent"
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${index}`}
        id={`faq-question-${index}`}
      >
        <span className={cn(
          'text-base font-medium pr-4 transition-colors duration-200',
          isOpen ? 'text-[rgb(var(--primary))]' : 'text-slate-900'
        )}>
          {question}
        </span>
        <ChevronDown
          className={cn(
            'w-5 h-5 flex-shrink-0 transition-transform duration-300 text-slate-400',
            isOpen && 'rotate-180 text-[rgb(var(--primary))]'
          )}
        />
      </button>
      <div
        id={`faq-answer-${index}`}
        role="region"
        aria-labelledby={`faq-question-${index}`}
        className={cn(
          'overflow-hidden transition-all duration-300 ease-out',
          isOpen ? 'max-h-96 opacity-100 pb-5' : 'max-h-0 opacity-0'
        )}
      >
        <p className="text-slate-600 leading-relaxed px-1">
          {answer}
        </p>
      </div>
    </div>
  );
};

/**
 * FAQs Page Component
 */
const FaqsPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(faqCategories[0]?.id || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState({});

  // Get all FAQ items for search
  const allFaqItems = useMemo(() => getAllFaqItems(), []);

  // Filter FAQs based on search
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return null;
    
    const query = searchQuery.toLowerCase();
    return allFaqItems.filter(
      item => item.q.toLowerCase().includes(query) || item.a.toLowerCase().includes(query)
    );
  }, [searchQuery, allFaqItems]);

  // Get current category items
  const currentCategoryItems = useMemo(() => {
    const category = faqCategories.find(c => c.id === activeCategory);
    return category?.items || [];
  }, [activeCategory]);

  // Toggle FAQ item
  const toggleItem = useCallback((itemKey) => {
    setOpenItems(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  }, []);

  // Handle category change
  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setSearchQuery('');
  };

  // Generate FAQ structured data for SEO
  const faqStructuredData = useMemo(() => {
    const items = filteredItems || currentCategoryItems;
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': items.map(item => ({
        '@type': 'Question',
        'name': item.q,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': item.a
        }
      }))
    };
  }, [filteredItems, currentCategoryItems]);

  // Inject structured data
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(faqStructuredData);
    script.id = 'faq-structured-data';
    
    // Remove existing script if any
    const existing = document.getElementById('faq-structured-data');
    if (existing) existing.remove();
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById('faq-structured-data');
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, [faqStructuredData]);

  return (
    <div className="min-h-screen" data-testid="faqs-page">
      <Header />

      {/* Hero Section */}
      <section className="py-16 md:py-20 bg-background-paper">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex justify-center mb-8">
              <img 
                src="https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/adbkjsqh_617A111F-E509-41D3-BBD3-84A5346C0488.png"
                alt="FAQ"
                className="w-32 h-32 md:w-40 md:h-40 object-contain"
              />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-[rgb(var(--foreground-muted))]">
              Find answers to common questions about investing with Flippex, our platform, and how it all works.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto mt-10">
            <div className="relative">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-[rgb(var(--foreground-muted))]" />
              <Input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-14 h-14 text-base bg-white/5 border-white/10 focus:border-[rgb(var(--primary))] rounded-full"
                aria-label="Search FAQs"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          {/* Search Results Mode */}
          {searchQuery.trim() && filteredItems ? (
            <div className="max-w-3xl mx-auto">
              <p className="text-sm text-[rgb(var(--foreground-muted))] mb-6">
                {filteredItems.length} result{filteredItems.length !== 1 ? 's' : ''} for &quot;{searchQuery}&quot;
              </p>
              {filteredItems.length > 0 ? (
                <div className="bg-white/5 rounded-2xl border border-white/10 p-6 md:p-8">
                  {filteredItems.map((item, index) => (
                    <div key={`search-${index}`}>
                      <span className="text-xs text-[rgb(var(--accent))] font-medium mb-2 block">
                        {item.categoryTitle}
                      </span>
                      <FaqItem
                        question={item.q}
                        answer={item.a}
                        isOpen={openItems[`search-${index}`]}
                        onToggle={() => toggleItem(`search-${index}`)}
                        index={`search-${index}`}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-[rgb(var(--foreground-muted))]">
                    No results found. Try different keywords or browse categories below.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setSearchQuery('')}
                  >
                    Clear Search
                  </Button>
                </div>
              )}
            </div>
          ) : (
            /* Category Mode */
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              {/* Category Navigation - Desktop Sidebar / Mobile Chips */}
              <div className="lg:w-64 flex-shrink-0">
                {/* Mobile: Horizontal scrollable chips */}
                <div className="lg:hidden overflow-x-auto pb-4 -mx-6 px-6">
                  <div className="flex gap-2 min-w-max">
                    {faqCategories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => handleCategoryChange(category.id)}
                        className={cn(
                          'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 bg-transparent',
                          activeCategory === category.id
                            ? 'bg-[rgb(var(--primary))] text-white'
                            : 'bg-white/5 text-[rgb(var(--foreground-muted))] hover:bg-white/10'
                        )}
                      >
                        {category.title}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Desktop: Sidebar navigation */}
                <div className="hidden lg:block sticky top-24">
                  <h3 className="text-sm font-semibold text-[rgb(var(--foreground-muted))] uppercase tracking-wide mb-4">
                    Categories
                  </h3>
                  <nav className="space-y-1">
                    {faqCategories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => handleCategoryChange(category.id)}
                        className={cn(
                          'w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 bg-transparent',
                          activeCategory === category.id
                            ? 'bg-[rgb(var(--primary))]/10 text-[rgb(var(--primary))] border-l-2 border-[rgb(var(--primary))]'
                            : 'text-[rgb(var(--foreground-muted))] hover:bg-white/5 hover:text-white'
                        )}
                      >
                        {category.title}
                        <span className="ml-2 text-xs opacity-60">
                          ({category.items.length})
                        </span>
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              {/* FAQ Items */}
              <div className="flex-1 max-w-3xl">
                <h2 className="text-2xl font-heading font-semibold mb-6">
                  {faqCategories.find(c => c.id === activeCategory)?.title}
                </h2>
                <div className="bg-white/5 rounded-2xl border border-white/10 p-6 md:p-8">
                  {currentCategoryItems.map((item, index) => (
                    <FaqItem
                      key={`${activeCategory}-${index}`}
                      question={item.q}
                      answer={item.a}
                      isOpen={openItems[`${activeCategory}-${index}`]}
                      onToggle={() => toggleItem(`${activeCategory}-${index}`)}
                      index={`${activeCategory}-${index}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-16 bg-background-paper">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[rgb(var(--accent))]/10 mb-6">
              <MessageCircle className="w-7 h-7 text-[rgb(var(--accent))]" />
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4">
              Still Have Questions?
            </h2>
            <p className="text-[rgb(var(--foreground-muted))] mb-8">
              Can&apos;t find what you&apos;re looking for? Our team is here to help.
            </p>
            <Button
              size="lg"
              className="bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary-hover))] rounded-full px-8"
              onClick={() => navigate('/contact')}
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FaqsPage;
